import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE } from '../api-base.token';
import { QuickConversationRequset } from './QuickConversationRequest';
import {
  ConversationDetail,
  ConversationListResponse,
  ConversationTurn,
  CreateConversationRequest,
  CreateTurnRequest,
  ConversationSummary,
} from './models';
import { WORKSPACE_ID } from '../config/known-headers-keys';

const path = 'conversations';

@Injectable({
  providedIn: 'root',
})
export class ConversationsHttpService {
  constructor(
    private readonly httpClient: HttpClient,
    @Inject(API_BASE)
    private readonly apiBase: string
  ) {}

  createQuickConversation(request: QuickConversationRequset) {
    const url = Location.joinWithSlash(this.apiBase, `${path}/quick`);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.httpClient.post<string>(url, request, { responseType: 'text' as any });
  }

  getConversations() {
    const url = Location.joinWithSlash(this.apiBase, path);
    return this.httpClient.get<ConversationListResponse>(url);
  }

  createConversation(request: CreateConversationRequest) {
    const url = Location.joinWithSlash(this.apiBase, path);
    return this.httpClient.post<ConversationSummary>(url, request);
  }

  getConversation(conversationId: string) {
    const url = Location.joinWithSlash(this.apiBase, `${path}/${conversationId}`);
    return this.httpClient.get<ConversationDetail>(url);
  }

  addTurn(conversationId: string, request: CreateTurnRequest) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${conversationId}/turns`
    );

    return this.httpClient.post<ConversationTurn>(url, request);
  }

  deleteConversation(conversationId: string) {
    const url = Location.joinWithSlash(this.apiBase, `${path}/${conversationId}`);
    return this.httpClient.delete<void>(url);
  }

  streamTurn(conversationId: string, request: CreateTurnRequest, userContextService: any) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${conversationId}/turns/stream`
    );

    return new Observable<string>((observer) => {
      const controller = new AbortController();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      const accessToken = userContextService.getAccessToken();
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const workspaceId = userContextService.getSelectedWorkspace()?.id;
      if (workspaceId) {
        headers[WORKSPACE_ID] = workspaceId;
      }

      fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.body) {
            throw new Error('No response body received');
          }

          if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Failed to stream response');
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder('utf-8');
          let buffer = '';

          const processChunk = (): Promise<void> =>
            reader.read().then(({ done, value }) => {
              if (done) {
                observer.complete();
                return;
              }

              buffer += decoder.decode(value, { stream: true });
              const events = buffer.split('\n\n');
              buffer = events.pop() ?? '';

              for (const event of events) {
                if (!event.startsWith('data:')) {
                  continue;
                }

                const payloadText = event.replace(/^data:\s*/, '');
                if (!payloadText) {
                  continue;
                }

                if (payloadText === '[DONE]') {
                  observer.complete();
                  return;
                }

                try {
                  const payload = JSON.parse(payloadText) as {
                    content?: string;
                    done?: boolean;
                    error?: string;
                  };

                  if (payload.error) {
                    observer.error(payload.error);
                    return;
                  }

                  if (payload.done) {
                    observer.complete();
                    return;
                  }

                  if (payload.content) {
                    observer.next(payload.content);
                  }
                } catch (error) {
                  observer.error(error);
                  return;
                }
              }

              return processChunk();
            });

          return processChunk();
        })
        .catch((error) => observer.error(error));

      return () => controller.abort();
    });
  }
}
